import axios from "axios";
import { BASE_URL } from "../constants";
import store from "../store";
import { logout, updateTokens } from "../store/slices/authSlice";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  }

  failedQueue = [];
};

// Request interceptor to add token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    throw error;
  }
);

// Response interceptor to handle token expiration
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if error is due to expired or invalid token
    // Handle 401 errors that haven't been retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for login and refresh-token endpoints
      if (
        originalRequest.url?.includes("/login") ||
        originalRequest.url?.includes("/refresh-token")
      ) {
        throw error;
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = "Bearer " + token;
            return axios(originalRequest);
          })
          .catch((err) => {
            throw err;
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) {
          // No refresh token, redirect to login
          store.dispatch(logout());
          window.location.href = "/login";
          throw error;
        }

        // Call refresh token endpoint
        const response = await axios.post(`${BASE_URL}/refresh-token`, {
          refreshToken,
        });

        if (response.data.status === 200) {
          const { token, refreshToken: newRefreshToken } = response.data;

          // Update tokens in Redux store and localStorage
          store.dispatch(
            updateTokens({
              token,
              refreshToken: newRefreshToken,
            })
          );

          // Update authorization header
          axios.defaults.headers.common["Authorization"] = "Bearer " + token;
          originalRequest.headers.Authorization = "Bearer " + token;

          // Process queued requests
          processQueue(null, token);
          isRefreshing = false;

          // Retry original request
          return axios(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh failed, logout via Redux and redirect to login
        store.dispatch(logout());
        window.location.href = "/login";
        throw refreshError;
      }
    }

    throw error;
  }
);

export { default } from "axios";
